/**
 * Supabase車両サービス
 * LandTransporter、VehicleModel、VehicleType、CompanyVehicleModelSettings管理を担当
 */

import { SupabaseClient } from '@supabase/supabase-js';
import logger from '../../utils/logger';
import { getSupabaseClient } from '../../utils/supabase';
import {
  LandTransporter,
  VehicleModel,
  VehicleModelDataSource,
  VehicleType,
  CompanyVehicleModelSettings,
  WorkingTimeSlot,
} from '../../types';
import { DataServiceError, ErrorCodes } from '../dataService';
import { DemoDataFallbackService } from '../demoDataFallback';

export class SupabaseVehicleService {
  public supabase: SupabaseClient | null = null;
  public currentCompanyId: string;

  constructor() {
    this.currentCompanyId =
      import.meta.env.VITE_DEMO_MODE === 'true' ? 'e32a0499-6456-44ac-9b16-b0001098c259' : '';
    this.initializeSupabase();
  }

  private initializeSupabase() {
    try {
      this.supabase = getSupabaseClient();
      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }
      logger.info('✅ SupabaseVehicleService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ SupabaseVehicleService initialization failed:', error);
    }
  }

  private ensureSupabaseClient() {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
  }

  /**
   * テスト専用メソッド: Supabaseクライアントを設定
   * プロダクションコードでは使用しないこと
   * @param client - 設定するSupabaseクライアント（テスト用）
   */
  public setSupabaseClientForTest(client: SupabaseClient | null): void {
    this.supabase = client;
  }

  /**
   * データベースのworking_daysデータをLandTransporter.workingDays形式に変換
   */
  private parseWorkingDays(workingDaysData: any): LandTransporter['workingDays'] {
    if (!workingDaysData || typeof workingDaysData !== 'object') {
      // デフォルトの空の勤務時間を返す
      return {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null,
      };
    }

    // 旧形式の判定（boolean値を持つ短縮名）
    const isLegacyFormat =
      typeof workingDaysData.mon === 'boolean' ||
      typeof workingDaysData.tue === 'boolean' ||
      typeof workingDaysData.wed === 'boolean' ||
      typeof workingDaysData.thu === 'boolean' ||
      typeof workingDaysData.fri === 'boolean' ||
      typeof workingDaysData.sat === 'boolean' ||
      typeof workingDaysData.sun === 'boolean';

    if (isLegacyFormat) {
      // 旧形式（boolean）の処理
      logger.debug('parseWorkingDays: 旧形式（boolean）データを検出、変換中');
      return {
        monday: workingDaysData.mon === true ? { startTime: '09:00', endTime: '18:00' } : null,
        tuesday: workingDaysData.tue === true ? { startTime: '09:00', endTime: '18:00' } : null,
        wednesday: workingDaysData.wed === true ? { startTime: '09:00', endTime: '18:00' } : null,
        thursday: workingDaysData.thu === true ? { startTime: '09:00', endTime: '18:00' } : null,
        friday: workingDaysData.fri === true ? { startTime: '09:00', endTime: '18:00' } : null,
        saturday: workingDaysData.sat === true ? { startTime: '09:00', endTime: '18:00' } : null,
        sunday: workingDaysData.sun === true ? { startTime: '09:00', endTime: '18:00' } : null,
      };
    }

    // 新形式（時間付き）の処理
    const parseTimeSlot = (timeSlotData: any): WorkingTimeSlot | null => {
      if (!timeSlotData || !timeSlotData.startTime || !timeSlotData.endTime) {
        return null;
      }
      return {
        startTime: timeSlotData.startTime,
        endTime: timeSlotData.endTime,
      };
    };

    logger.debug('parseWorkingDays: 新形式（時間付き）データを処理中');
    return {
      monday: parseTimeSlot(workingDaysData.monday),
      tuesday: parseTimeSlot(workingDaysData.tuesday),
      wednesday: parseTimeSlot(workingDaysData.wednesday),
      thursday: parseTimeSlot(workingDaysData.thursday),
      friday: parseTimeSlot(workingDaysData.friday),
      saturday: parseTimeSlot(workingDaysData.saturday),
      sunday: parseTimeSlot(workingDaysData.sunday),
    };
  }

  // =============================================================================
  // LandTransporter関連メソッド
  // =============================================================================

  /**
   * 陸送員一覧を取得
   * @returns 陸送員一覧
   */
  async getLandTransporters(): Promise<LandTransporter[]> {
    logger.info('🚚 SupabaseVehicleService: getLandTransporters開始');
    this.ensureSupabaseClient();

    if (!this.supabase) {
      logger.warn('Supabaseが利用できないため、ダミーデータを返します');
      return DemoDataFallbackService.getLandTransporters();
    }

    try {
      const { data, error } = await this.supabase
        .from('company_land_transporters')
        .select(
          `
          id,
          name,
          email,
          phone,
          address,
          license_number,
          license_expiry_date,
          experience_years,
          status,
          company_id,
          drivable_vehicle_types,
          working_days,
          has_license_for_large_vehicles,
          can_work_night_shift,
          can_work_long_distance,
          has_temporary_license,
          is_at_only,
          notes,
          special_notes,
          created_at,
          updated_at
        `
        )
        .eq('company_id', this.currentCompanyId)
        .order('name');

      if (error) {
        logger.error('LandTransporters取得エラー:', error);
        throw new DataServiceError(
          `陸送員データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const landTransporters: LandTransporter[] = data.map(
        (row: any): LandTransporter => ({
          id: row.id,
          name: row.name,
          phone: row.phone,
          address: row.address || '',
          status: row.status,
          drivableVehicleTypes: Array.isArray(row.drivable_vehicle_types)
            ? row.drivable_vehicle_types
            : [],
          workingDays: this.parseWorkingDays(row.working_days),
          hasLicenseForLargeVehicles: row.has_license_for_large_vehicles || false,
          canWorkNightShift: row.can_work_night_shift || false,
          canWorkLongDistance: row.can_work_long_distance || false,
          hasTemporaryLicense: row.has_temporary_license || false,
          isATOnly: row.is_at_only || false,
          isSolicitationProhibited: row.is_solicitation_prohibited || false,
          isDesignatedStoreProhibited: row.is_designated_store_prohibited || false,
          notes: row.notes || '',
          // FL-270: Google Maps統合 - 最寄駅情報
          nearestStation: row.nearest_station || undefined,
          nearestStationPlaceId: row.nearest_station_place_id || undefined,
          nearestStationLat: row.nearest_station_lat || undefined,
          nearestStationLng: row.nearest_station_lng || undefined,
        })
      );

      logger.info('✅ SupabaseVehicleService: getLandTransporters完了', {
        count: landTransporters.length,
        companyId: this.currentCompanyId,
      });

      return landTransporters;
    } catch (error) {
      logger.error('❌ SupabaseMasterDataService: getLandTransporters実行エラー:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送員データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい陸送員を作成
   * @param transporter 陸送員データ（IDなし）
   * @returns 作成された陸送員データ
   */
  async createLandTransporter(transporter: Omit<LandTransporter, 'id'>): Promise<LandTransporter> {
    logger.info('🚀 SupabaseVehicleService: createLandTransporter開始:', transporter.name);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_land_transporters')
        .insert([
          {
            name: transporter.name,
            phone: transporter.phone,
            address: transporter.address,
            status: transporter.status,
            drivable_vehicle_types: transporter.drivableVehicleTypes,
            working_days: transporter.workingDays,
            has_license_for_large_vehicles: transporter.hasLicenseForLargeVehicles,
            can_work_night_shift: transporter.canWorkNightShift,
            can_work_long_distance: transporter.canWorkLongDistance,
            has_temporary_license: transporter.hasTemporaryLicense,
            is_at_only: transporter.isATOnly,
            is_solicitation_prohibited: transporter.isSolicitationProhibited,
            is_designated_store_prohibited: transporter.isDesignatedStoreProhibited,
            notes: transporter.notes,
            // FL-270: Google Maps統合 - 最寄駅情報
            nearest_station: transporter.nearestStation,
            nearest_station_place_id: transporter.nearestStationPlaceId,
            nearest_station_lat: transporter.nearestStationLat,
            nearest_station_lng: transporter.nearestStationLng,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `陸送員の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      const createdTransporter: LandTransporter = {
        id: data.id,
        name: data.name,
        phone: data.phone,
        address: data.address || '',
        status: data.status,
        drivableVehicleTypes: Array.isArray(data.drivable_vehicle_types)
          ? data.drivable_vehicle_types
          : [],
        workingDays: this.parseWorkingDays(data.working_days),
        hasLicenseForLargeVehicles: data.has_license_for_large_vehicles || false,
        canWorkNightShift: data.can_work_night_shift || false,
        canWorkLongDistance: data.can_work_long_distance || false,
        hasTemporaryLicense: data.has_temporary_license || false,
        isATOnly: data.is_at_only || false,
        isSolicitationProhibited: data.is_solicitation_prohibited || false,
        isDesignatedStoreProhibited: data.is_designated_store_prohibited || false,
        notes: data.notes || '',
        // FL-270: Google Maps統合 - 最寄駅情報
        nearestStation: data.nearest_station || undefined,
        nearestStationPlaceId: data.nearest_station_place_id || undefined,
        nearestStationLat: data.nearest_station_lat || undefined,
        nearestStationLng: data.nearest_station_lng || undefined,
      };

      logger.info(
        `✅ SupabaseVehicleService: createLandTransporter完了 - ${createdTransporter.name}`
      );
      return createdTransporter;
    } catch (error) {
      logger.error('❌ SupabaseVehicleService: createLandTransporter失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送員の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 陸送員を更新
   * @param id 陸送員ID
   * @param transporter 更新データ
   * @returns 更新された陸送員データ
   */
  async updateLandTransporter(
    id: string,
    transporter: Partial<LandTransporter>
  ): Promise<LandTransporter> {
    logger.info(`🔧 SupabaseVehicleService: updateLandTransporter開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_land_transporters')
        .update({
          name: transporter.name,
          phone: transporter.phone,
          address: transporter.address,
          status: transporter.status,
          drivable_vehicle_types: transporter.drivableVehicleTypes,
          working_days: transporter.workingDays,
          has_license_for_large_vehicles: transporter.hasLicenseForLargeVehicles,
          can_work_night_shift: transporter.canWorkNightShift,
          can_work_long_distance: transporter.canWorkLongDistance,
          has_temporary_license: transporter.hasTemporaryLicense,
          is_at_only: transporter.isATOnly,
          is_solicitation_prohibited: transporter.isSolicitationProhibited,
          is_designated_store_prohibited: transporter.isDesignatedStoreProhibited,
          notes: transporter.notes,
          // FL-270: Google Maps統合 - 最寄駅情報
          nearest_station: transporter.nearestStation,
          nearest_station_place_id: transporter.nearestStationPlaceId,
          nearest_station_lat: transporter.nearestStationLat,
          nearest_station_lng: transporter.nearestStationLng,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `陸送員の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      const updatedTransporter: LandTransporter = {
        id: data.id,
        name: data.name,
        phone: data.phone,
        address: data.address || '',
        status: data.status,
        drivableVehicleTypes: Array.isArray(data.drivable_vehicle_types)
          ? data.drivable_vehicle_types
          : [],
        workingDays: this.parseWorkingDays(data.working_days),
        hasLicenseForLargeVehicles: data.has_license_for_large_vehicles || false,
        canWorkNightShift: data.can_work_night_shift || false,
        canWorkLongDistance: data.can_work_long_distance || false,
        hasTemporaryLicense: data.has_temporary_license || false,
        isATOnly: data.is_at_only || false,
        isSolicitationProhibited: data.is_solicitation_prohibited || false,
        isDesignatedStoreProhibited: data.is_designated_store_prohibited || false,
        notes: data.notes || '',
        // FL-270: Google Maps統合 - 最寄駅情報
        nearestStation: data.nearest_station || undefined,
        nearestStationPlaceId: data.nearest_station_place_id || undefined,
        nearestStationLat: data.nearest_station_lat || undefined,
        nearestStationLng: data.nearest_station_lng || undefined,
      };

      logger.info(
        `✅ SupabaseVehicleService: updateLandTransporter完了 - ${updatedTransporter.name}`
      );
      return updatedTransporter;
    } catch (error) {
      logger.error('❌ SupabaseVehicleService: updateLandTransporter失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送員の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 陸送員を削除
   * @param id 陸送員ID
   */
  async deleteLandTransporter(id: string): Promise<void> {
    logger.info(`🗑️ SupabaseVehicleService: deleteLandTransporter開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_land_transporters')
        .delete()
        .eq('id', id);

      if (error) {
        throw new DataServiceError(
          `陸送員の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseVehicleService: deleteLandTransporter完了 - ${id}`);
    } catch (error) {
      logger.error('❌ SupabaseVehicleService: deleteLandTransporter失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送員の削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // VehicleModel関連メソッド
  // =============================================================================

  /**
   * 車両モデル一覧を取得
   * @returns 車両モデル一覧
   */
  async getVehicleModels(): Promise<VehicleModel[]> {
    logger.info('[getVehicleModels] 開始 - CI環境:', import.meta.env.CI === 'true');
    this.ensureSupabaseClient();

    if (!this.supabase) {
      logger.warn('Supabaseが利用できないため、空配列を返します');
      return [];
    }

    try {
      let data: any;

      // 最初に新しいスキーマで試行（company_idフィルターを削除）
      logger.info('[getVehicleModels] 新しいスキーマでクエリ実行を試行');

      const newSchemaResult = await this.supabase
        .from('vehicle_models')
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source
        `
        )
        .order('name');

      logger.info('[getVehicleModels] 新しいスキーマクエリ結果:', {
        error: newSchemaResult.error?.message,
        dataCount: newSchemaResult.data?.length,
      });

      if (newSchemaResult.error) {
        logger.warn(
          '[getVehicleModels] 新しいスキーマでの取得に失敗、旧スキーマで再試行:',
          newSchemaResult.error
        );

        // 旧スキーマで再試行
        const fallbackResult = await this.supabase.from('vehicle_models').select('*').order('name');

        logger.info('[getVehicleModels] 旧スキーマクエリ結果:', {
          error: fallbackResult.error?.message,
          dataCount: fallbackResult.data?.length,
        });

        if (fallbackResult.error) {
          throw fallbackResult.error;
        }
        data = fallbackResult.data;
      } else {
        data = newSchemaResult.data;
      }

      if (!data) {
        logger.error('[getVehicleModels] データが取得できませんでした');
        return [];
      }

      logger.info('[getVehicleModels] 成功 - データ件数:', data?.length || 0);

      const vehicleModels = data.map((item: any) => ({
        id: item.id,
        name: item.name,
        manufacturerId: item.manufacturer_id,
        vehicleTypeId: item.vehicle_type_id,
        powertrain: item.powertrain,
        vehicleTags: item.vehicle_tags || [],
        dataSource: (item.data_source as VehicleModelDataSource) || 'custom',
      }));

      return vehicleModels;
    } catch (error) {
      logger.error('VehicleModels取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Models取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  /**
   * 新しい車両モデルを作成
   * @param model 車両モデルデータ（IDなし）
   * @returns 作成された車両モデルデータ
   */
  async createVehicleModel(model: Omit<VehicleModel, 'id'>): Promise<VehicleModel> {
    logger.info('🚀 SupabaseVehicleService: createVehicleModel開始:', model.name);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_models')
        .insert([
          {
            name: model.name,
            manufacturer_id: model.manufacturerId,
            vehicle_type_id: model.vehicleTypeId,
            powertrain: model.powertrain,
            vehicle_tags: model.vehicleTags || [],
            data_source: model.dataSource,
          },
        ])
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source
        `
        )
        .single();

      if (error) {
        logger.error('VehicleModel作成エラー:', error);
        throw new DataServiceError(
          `車両モデルの作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      const createdModel: VehicleModel = {
        id: data.id,
        name: data.name,
        manufacturerId: data.manufacturer_id,
        vehicleTypeId: data.vehicle_type_id,
        powertrain: data.powertrain,
        vehicleTags: data.vehicle_tags || [],
        dataSource: data.data_source as VehicleModelDataSource,
      };

      logger.info(`✅ SupabaseVehicleService: createVehicleModel完了 - ${createdModel.name}`);
      return createdModel;
    } catch (error) {
      logger.error('VehicleModel作成エラー:', error);
      throw new DataServiceError(
        '車両モデルの作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 車両モデルを更新
   * @param id 車両モデルID
   * @param model 更新データ
   * @returns 更新された車両モデルデータ
   */
  async updateVehicleModel(id: string, model: Partial<VehicleModel>): Promise<VehicleModel> {
    logger.info(`🔧 SupabaseVehicleService: updateVehicleModel開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const updateData: any = {};
      if (model.name !== undefined) updateData.name = model.name;
      if (model.manufacturerId !== undefined) updateData.manufacturer_id = model.manufacturerId;
      if (model.vehicleTypeId !== undefined) updateData.vehicle_type_id = model.vehicleTypeId;
      if (model.powertrain !== undefined) updateData.powertrain = model.powertrain;
      if (model.dataSource !== undefined) updateData.data_source = model.dataSource;
      if ('vehicleTags' in model) updateData.vehicle_tags = model.vehicleTags || null;

      const { data, error } = await this.supabase!.from('vehicle_models')
        .update(updateData)
        .eq('id', id)
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source
        `
        )
        .single();

      if (error) {
        logger.error('VehicleModel更新エラー:', error);
        throw new DataServiceError(
          `車両モデルの更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      const updatedModel: VehicleModel = {
        id: data.id,
        name: data.name,
        manufacturerId: data.manufacturer_id,
        vehicleTypeId: data.vehicle_type_id,
        powertrain: data.powertrain,
        vehicleTags: data.vehicle_tags || [],
        dataSource: data.data_source as VehicleModelDataSource,
      };

      logger.info(`✅ SupabaseVehicleService: updateVehicleModel完了 - ${updatedModel.name}`);
      return updatedModel;
    } catch (error) {
      logger.error('VehicleModel更新エラー:', error);
      throw new DataServiceError(
        '車両モデルの更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 車両モデルを削除
   * @param id 車両モデルID
   */
  async deleteVehicleModel(id: string): Promise<void> {
    logger.info(`🗑️ SupabaseVehicleService: deleteVehicleModel開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      // まず削除対象のデータを取得して、ラクハイ提供データかチェック
      const { data: vehicleModel, error: fetchError } = await this.supabase!.from('vehicle_models')
        .select('data_source')
        .eq('id', id)
        .single();

      if (fetchError) {
        logger.error('VehicleModel取得エラー:', fetchError);
        throw new DataServiceError(
          '車両モデルの取得に失敗しました',
          ErrorCodes.DATA_ACCESS_ERROR,
          fetchError
        );
      }

      if (vehicleModel?.data_source === VehicleModelDataSource.RAKUHAI_PROVIDED) {
        throw new DataServiceError(
          'ラクハイ提供の車両モデルは削除できません',
          ErrorCodes.PERMISSION_DENIED,
          new Error('Cannot delete rakuhai-provided vehicle model')
        );
      }

      const { error } = await this.supabase!.from('vehicle_models').delete().eq('id', id);

      if (error) {
        logger.error('VehicleModel削除エラー:', error);
        throw new DataServiceError(
          `車両モデルの削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseVehicleService: deleteVehicleModel完了 - ${id}`);
    } catch (error) {
      logger.error('VehicleModel削除エラー:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'Vehicle Model削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // VehicleType関連メソッド
  // =============================================================================

  /**
   * 車種一覧を取得
   * @returns 車種一覧
   */
  async getVehicleTypes(): Promise<VehicleType[]> {
    logger.info('🚗 SupabaseVehicleService: getVehicleTypes開始');
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_types')
        .select(
          `
          id,
          name,
          company_id,
          created_at,
          updated_at
        `
        )
        .eq('company_id', this.currentCompanyId)
        .order('name');

      if (error) {
        logger.error('Vehicle types取得エラー:', error);
        throw new DataServiceError(
          `車種データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const vehicleTypes = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
      }));

      logger.info(`✅ SupabaseVehicleService: getVehicleTypes完了 - ${vehicleTypes.length}件取得`);
      return vehicleTypes;
    } catch (error) {
      logger.error('VehicleTypes取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Types取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  /**
   * 新しい車種を作成
   * @param type 車種データ（IDなし）
   * @returns 作成された車種データ
   */
  async createVehicleType(type: Omit<VehicleType, 'id'>): Promise<VehicleType> {
    logger.info('🚀 SupabaseVehicleService: createVehicleType開始:', type.name);
    this.ensureSupabaseClient();

    // 権限チェック（例）
    if (!this.currentCompanyId) {
      throw new DataServiceError(
        '車種作成の権限がありません',
        ErrorCodes.PERMISSION_DENIED,
        new Error('Insufficient permissions for vehicle type creation')
      );
    }

    try {
      const { data, error } = await this.supabase!.from('vehicle_types')
        .insert([
          {
            name: type.name,
          },
        ])
        .select(
          `
          id,
          name,
          company_id,
          created_at,
          updated_at
        `
        )
        .single();

      if (error) {
        logger.error('VehicleType作成エラー:', error);
        throw new DataServiceError(
          `車種の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      const createdType: VehicleType = {
        id: data.id,
        name: data.name,
      };

      logger.info(`✅ SupabaseVehicleService: createVehicleType完了 - ${createdType.name}`);
      return createdType;
    } catch (error) {
      logger.error('VehicleType作成エラー:', error);
      throw new DataServiceError(
        'Vehicle Type作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 車種を更新
   * @param id 車種ID
   * @param type 更新データ
   * @returns 更新された車種データ
   */
  async updateVehicleType(_id: string, _type: Partial<VehicleType>): Promise<VehicleType> {
    // 現在は未実装
    throw new DataServiceError(
      '車種更新の権限がありません',
      ErrorCodes.PERMISSION_DENIED,
      new Error('Insufficient permissions for vehicle type update')
    );
  }

  /**
   * 車種を削除
   * @param id 車種ID
   */
  async deleteVehicleType(_id: string): Promise<void> {
    // 現在は未実装
    throw new DataServiceError(
      '車種削除の権限がありません',
      ErrorCodes.PERMISSION_DENIED,
      new Error('Insufficient permissions for vehicle type deletion')
    );
  }

  // =============================================================================
  // CompanyVehicleModelSettings関連メソッド
  // =============================================================================

  /**
   * 会社車両モデル設定を取得
   * @param companyId 会社ID
   * @returns 車両モデル設定またはnull
   */
  async getCompanyVehicleModelSettings(
    companyId: string
  ): Promise<CompanyVehicleModelSettings | null> {
    logger.info(
      `📋 SupabaseVehicleService: getCompanyVehicleModelSettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_vehicle_model_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `車両モデル設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      const settings: CompanyVehicleModelSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: data.data_source_preferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: data.country_preferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: data.powertrain_preferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: data.tag_preferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseVehicleService: getCompanyVehicleModelSettings完了`);
      return settings;
    } catch (error) {
      logger.error('❌ SupabaseVehicleService: getCompanyVehicleModelSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '車両モデル設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい会社車両モデル設定を作成
   * @param settings 設定データ（IDなし）
   * @returns 作成された設定データ
   */
  async createCompanyVehicleModelSettings(
    settings: Omit<CompanyVehicleModelSettings, 'id'>
  ): Promise<CompanyVehicleModelSettings> {
    logger.info('🚀 SupabaseVehicleService: createCompanyVehicleModelSettings開始');
    this.ensureSupabaseClient();

    if (!this.supabase) {
      const mockSettings: CompanyVehicleModelSettings = {
        id: 'mock-id',
        ...settings,
      };
      logger.warn('Supabaseが利用できないため、CompanyVehicleModelSettings作成をスキップします');
      return mockSettings;
    }

    try {
      const { data, error } = await this.supabase
        .from('company_vehicle_model_settings')
        .insert([
          {
            company_id: settings.companyId,
            enabled_model_ids: settings.enabledModelIds,
            data_source_preferences: settings.dataSourcePreferences,
            country_preferences: settings.countryPreferences,
            powertrain_preferences: settings.powertrainPreferences,
            tag_preferences: settings.tagPreferences,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `車両モデル設定の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      // データベースからの戻り値をCompanyVehicleModelSettings型に変換
      const createdSettings: CompanyVehicleModelSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: data.data_source_preferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: data.country_preferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: data.powertrain_preferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: data.tag_preferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(
        `✅ SupabaseVehicleService: createCompanyVehicleModelSettings完了 - ${createdSettings.id}`
      );
      return createdSettings;
    } catch (error) {
      logger.error('❌ SupabaseVehicleService: createCompanyVehicleModelSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '車両モデル設定の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 会社車両モデル設定を更新
   * @param id 設定ID
   * @param settings 更新データ
   * @returns 更新された設定データ
   */
  async updateCompanyVehicleModelSettings(
    id: string,
    settings: Partial<CompanyVehicleModelSettings>
  ): Promise<CompanyVehicleModelSettings> {
    logger.info(`🔧 SupabaseVehicleService: updateCompanyVehicleModelSettings開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    if (!this.supabase) {
      const mockSettings: CompanyVehicleModelSettings = {
        id,
        companyId: settings.companyId || '',
        enabledModelIds: settings.enabledModelIds || [],
        dataSourcePreferences: settings.dataSourcePreferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: settings.countryPreferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: settings.powertrainPreferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: settings.tagPreferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: settings.createdAt || new Date().toISOString(),
        updatedAt: settings.updatedAt || new Date().toISOString(),
      };
      logger.warn('Supabaseが利用できないため、CompanyVehicleModelSettings更新をスキップします');
      return mockSettings;
    }

    try {
      const updateData: any = {};
      if (settings.enabledModelIds !== undefined) {
        updateData.enabled_model_ids = settings.enabledModelIds;
      }
      if (settings.dataSourcePreferences !== undefined) {
        updateData.data_source_preferences = settings.dataSourcePreferences;
      }
      if (settings.countryPreferences !== undefined) {
        updateData.country_preferences = settings.countryPreferences;
      }
      if (settings.powertrainPreferences !== undefined) {
        updateData.powertrain_preferences = settings.powertrainPreferences;
      }
      if (settings.tagPreferences !== undefined) {
        updateData.tag_preferences = settings.tagPreferences;
      }

      const { data, error } = await this.supabase
        .from('company_vehicle_model_settings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('CompanyVehicleModelSettings更新エラー:', error);
        throw new DataServiceError(
          `車両モデル設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      // データベースからの戻り値をCompanyVehicleModelSettings型に変換
      const updatedSettings: CompanyVehicleModelSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: data.data_source_preferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: data.country_preferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: data.powertrain_preferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: data.tag_preferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(
        `✅ SupabaseVehicleService: updateCompanyVehicleModelSettings完了 - ${updatedSettings.id}`
      );
      return updatedSettings;
    } catch (error) {
      logger.error('CompanyVehicleModelSettings更新エラー:', error);
      throw new DataServiceError(
        '車両モデル設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 会社車両モデル設定を削除
   * @param id 設定ID
   */
  async deleteCompanyVehicleModelSettings(id: string): Promise<void> {
    logger.info(`🗑️ SupabaseVehicleService: deleteCompanyVehicleModelSettings開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    if (!this.supabase) {
      logger.warn('Supabaseが利用できないため、CompanyVehicleModelSettings削除をスキップします');
      return;
    }

    try {
      const { error } = await this.supabase
        .from('company_vehicle_model_settings')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('CompanyVehicleModelSettings削除エラー:', error);
        throw new DataServiceError(
          `車両モデル設定の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseVehicleService: deleteCompanyVehicleModelSettings完了 - ${id}`);
    } catch (error) {
      logger.error('CompanyVehicleModelSettings削除エラー:', error);
      throw new DataServiceError(
        '車両モデル設定の削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }
}
